import { extractPdfData } from "../../../services/aiAgents";
import * as staticDataService from "../../../services/staticDataService";
import Disabled from "../../common/Disabled";
import AppliedExcessRecorder from "./AppliedExcessRecorder";
import CentredLoader from "./CentredLoader";
import * as cdr from "./ClaimsDialog.reducer";
import ClaimsTransformer from "./ClaimsTransformer";
import ColumnMapper from "./ColumnMapper";
import PdfExtractionFlow from "./PdfExtractionFlow";
import SheetSelector, { MultiSheetSelector } from "./SheetSelector";
import ValueMapper from "./ValueMapper";
import { ValueMapperController } from "./ValueMapper.controller";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  Snackbar,
} from "@mui/material";
import FilesFileUpload from "components/FilesFileUpload";
import * as config from "config";
import { tabularMimeTypes, pdfMimeTypes } from "fileUtils";
import * as flags from "flags";
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useGetSheetAnalysisQuery,
  streamingClaimsAnalysis,
} from "services/aiAgents";
import {
  useGetClaimsColumnsQuery,
  useGetTableSummaryQuery,
} from "services/tablesService";
import * as utils from "utils";

const ClaimsUpload = ({
  uploadStarted,
  uploadCompleted,
  aborter,
  isAnalyzing,
  loadingMessage,
  useClaimsAIParsing,
  useAiPdfExtraction,
}) => {
  const onFileDrop = (id, file) => {
    uploadCompleted(id, file);
  };

  // Include PDFs in allowed mime types when PDF extraction flag is enabled
  const allowedMimeTypes = useAiPdfExtraction
    ? [...tabularMimeTypes, ...pdfMimeTypes]
    : tabularMimeTypes;

  // Update drop message to include PDF when feature flag is enabled
  const dropMessage = useAiPdfExtraction
    ? "Drop an .xlsx, .csv, .tsv, or .pdf file here to upload"
    : "Drop an .xlsx, .csv, or .tsv file here to upload";

  return (
    <Disabled ifReadOnly>
      <FilesFileUpload
        onDrop={onFileDrop}
        updateAsAtDate={null}
        asAtDate={null}
        recommendedAsAtDate={null}
        size={{ md: 4 }}
        waitText={false}
        mimeTypes={allowedMimeTypes}
        dropMessage={dropMessage}
        uploadInitiated={uploadStarted}
        aborter={aborter}
        showLoadingOverlay={useClaimsAIParsing && isAnalyzing && loadingMessage}
        loadingMessage={loadingMessage}
      />
    </Disabled>
  );
};

const ClaimsDialog = ({ startFileProcessingRef = null, noDropBox = false }) => {
  const [mappingConfig, setMappingConfig] = useState(cdr.clearMapping);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [step, setStep] = useState(0);

  const [tablesList, setTablesList] = useState(null);
  const [isConversionComplete, setIsConversionComplete] = useState(false);
  const [valueMapperControllers, setValueMapperControllers] = useState([]);
  const [targetColumnList, setTargetColumnList] = useState([]);
  const [targetColumnMap, setTargetColumnMap] = useState({});
  const [sourceHeaders, setSourceHeaders] = useState([]);
  const [allValuesMapped, setAllValuesMapped] = useState({});
  const [appliedExcessValid, setAppliedExcessValid] = useState(true);
  const [requiredColumns, setRequiredColumns] = useState([]);
  const [outputColums, setOutputColums] = useState([]);
  const [fileDownloadUrl, setFileDownloadUrl] = useState(null);
  const [sheetAnalysisResults, setSheetAnalysisResults] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isAnalysisComplete, setIsAnalysisComplete] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState("uploading");
  const [analysisHeaders, setAnalysisHeaders] = useState(null);

  // PDF extraction state
  const [showPdfFlow, setShowPdfFlow] = useState(false);
  const [pdfExtractionResult, setPdfExtractionResult] = useState(null);

  // Multi-sheet state
  const [selectedSheets, setSelectedSheets] = useState([]);
  const [isMultiSheetMode, setIsMultiSheetMode] = useState(false);
  const [multiSheetConfig, setMultiSheetConfig] = useState(null);
  const [currentSheetIndex, setCurrentSheetIndex] = useState(0);
  const [sheetConfigs, setSheetConfigs] = useState({}); // Per-sheet configurations

  // Multi-sheet processing state
  const [
    currentProcessingSheetIndex,
    setCurrentProcessingSheetIndex,
  ] = useState(0);
  const [completedSheets, setCompletedSheets] = useState([]);

  // Validation state for multi-sheet mode
  const [, setAllSheetsColumnMappingsValid] = useState(true);
  const [, setAllSheetsValueMappingsValid] = useState(true);

  const dialogContentRef = useRef();

  const {
    data: mappingConfiguration,
    status: mappingConfigurationStatus,
  } = useGetClaimsColumnsQuery();

  // Feature flags
  const useClaimsAIParsing = staticDataService.useFeatureFlag(
    flags.USE_CLAIMS_AI_PARSING,
    { default: false }
  );

  const useStreamingSheetAnalysis = staticDataService.useFeatureFlag(
    flags.USE_STREAMING_FOR_CLAIMS_AI_PARSING,
    { default: false }
  );

  const useAiPdfExtraction = staticDataService.useFeatureFlag(
    flags.AI_PDF_EXTRACTION,
    { default: false }
  );

  const stepRef = useRef(step);
  stepRef.current = step;

  // Helper functions defined early to avoid use-before-define warnings
  const shouldShowChooseDataStep =
    !useClaimsAIParsing ||
    (useClaimsAIParsing &&
      sheetAnalysisResults &&
      isAnalysisComplete &&
      sheetAnalysisResults.sheets &&
      sheetAnalysisResults.sheets.length > 1);

  const getChooseDataStepIndex = useCallback(
    () => (shouldShowChooseDataStep ? 1 : -1),
    [shouldShowChooseDataStep]
  );
  const getChooseColumnsStepIndex = useCallback(
    () => (shouldShowChooseDataStep ? 2 : 1),
    [shouldShowChooseDataStep]
  );

  const handleAnalysisResults = useCallback(
    (analysisData, isStreaming = false) => {
      setSheetAnalysisResults(analysisData);

      // Populate tablesList with available sheets from analysis
      if (analysisData.sheets) {
        const sheetNames = analysisData.sheets.map((sheet) => sheet.sheet_name);
        setTablesList(sheetNames);
      }

      // When AI parsing is enabled, show progress message but don't advance step yet
      if (useClaimsAIParsing) {
        if (analysisData.recommended_sheet) {
          setLoadingPhase(
            `Received recommended claims sheet: ${analysisData.recommended_sheet}`
          );
        }
        // Don't advance step or stop analyzing yet - wait for completion
        if (!isStreaming) {
          // Only advance for non-streaming (complete) analysis
          // Don't reset step if we're already past the initial analysis steps
          if (step <= 1) {
            const chooseDataStep = getChooseDataStepIndex();
            if (chooseDataStep !== -1) {
              setStep(chooseDataStep); // Go to Choose Data step
            } else {
              setStep(getChooseColumnsStepIndex()); // Skip directly to Choose Columns
            }
          }
          setIsAnalyzing(false);
        }
      } else {
        // Legacy behavior: auto-select recommended sheet and skip to Choose Columns
        if (analysisData.recommended_sheet && !isMultiSheetMode) {
          const recommendedSheet = analysisData.recommended_sheet;
          const sheetData = analysisData.sheets?.find(
            (s) => s.sheet_name === recommendedSheet
          );

          setMappingConfig((cd) =>
            cdr.setTableName(cd, { tableName: recommendedSheet })
          );

          if (sheetData?.header_row) {
            setAnalysisHeaders(sheetData.header_row);
          }

          if (sheetData?.table_start) {
            const row = Math.max(0, (sheetData.table_start.row || 1) - 1);
            const column = Math.max(
              0,
              (sheetData.table_start.column || sheetData.table_start.col || 1) -
                1
            );
            setMappingConfig((cd) =>
              cdr.setOrigin(cd, { origin: { row, column } })
            );
          } else {
            setMappingConfig((cd) =>
              cdr.setOrigin(cd, { origin: { row: 0, column: 0 } })
            );
          }

          // Auto-advance to Choose Columns step only if we're still on early steps
          const chooseColumnsStep = getChooseColumnsStepIndex();
          if (step <= chooseColumnsStep) {
            setLoadingPhase("processing");
            if (isStreaming) {
              setStep(chooseColumnsStep); // Skip Choose Data, go directly to Choose Columns
              setIsAnalyzing(false);
            } else {
              setTimeout(() => {
                setStep(chooseColumnsStep);
                setIsAnalyzing(false);
              }, 500);
            }
          } else {
            setIsAnalyzing(false);
          }
        } else {
          setIsAnalyzing(false);
        }
      }
    },
    [
      step,
      isMultiSheetMode,
      useClaimsAIParsing,
      getChooseDataStepIndex,
      getChooseColumnsStepIndex,
    ]
  );

  // Streaming analysis function
  const processStreamingAnalysis = useCallback(
    async (fileUrl) => {
      try {
        await streamingClaimsAnalysis(fileUrl, {
          onRecommendedSheetAnalysis: (formattedData) => {
            handleAnalysisResults(formattedData, true); // Streaming recommended sheet analysis
          },
          onComplete: (result) => {
            const currentStep = stepRef.current;
            setIsAnalysisComplete(true);

            // Always store the complete analysis data for potential "Prev" navigation
            setSheetAnalysisResults(result);
            if (result.sheets) {
              const sheetNames = result.sheets.map((sheet) => sheet.sheet_name);
              setTablesList(sheetNames);
            }

            // Show completion message and advance to appropriate step
            setLoadingPhase("Analysis complete");
            if (currentStep <= 1) {
              const chooseDataStep = getChooseDataStepIndex();
              if (chooseDataStep !== -1) {
                setStep(chooseDataStep); // Go to Choose Data step
              } else {
                setStep(getChooseColumnsStepIndex()); // Skip directly to Choose Columns
              }
            }
            setIsAnalyzing(false);
          },
          onError: (error) => {
            setIsAnalyzing(false);
          },
        });
      } catch (error) {
        setIsAnalyzing(false);
        // Don't auto-advance on error, let user proceed manually
      }
    },
    [handleAnalysisResults, getChooseDataStepIndex, getChooseColumnsStepIndex]
  );

  // Skip if: no URL, AI parsing disabled, streaming enabled, OR if it's a PDF file
  const isPdfFile = mappingConfig?.filename?.toLowerCase().endsWith(".pdf");
  const sheetAnalysisQuery = useGetSheetAnalysisQuery(fileDownloadUrl, {
    skip:
      !fileDownloadUrl ||
      !useClaimsAIParsing ||
      useStreamingSheetAnalysis ||
      isPdfFile,
  });

  useImperativeHandle(
    startFileProcessingRef,
    () => ({
      start: (fileId, filename) => {
        setMappingConfig((cd) => cdr.setFileId(cd, { fileId }));
        setMappingConfig((cd) => cdr.setFilename(cd, { filename }));
        const chooseDataStep = getChooseDataStepIndex();
        if (chooseDataStep !== -1) {
          setStep(chooseDataStep); // Go to Choose Data step
        } else {
          setStep(getChooseColumnsStepIndex()); // Skip directly to Choose Columns
        }
        setDialogOpen(true);
      },
    }),
    [getChooseDataStepIndex, getChooseColumnsStepIndex]
  );

  useEffect(() => {
    if (mappingConfigurationStatus === "fulfilled") {
      const columns = mappingConfiguration.columns;
      setOutputColums(columns);

      const reqCols = columns
        .filter((e) => e.required)
        .map((col) => col.column);
      setRequiredColumns(reqCols);

      const enumCols = columns.filter((e) => e.type === "enum");
      const controllers = [];
      for (const columnId in enumCols) {
        const column = enumCols[columnId];
        controllers.push(
          new ValueMapperController({
            key: column.column,
            label: column.display,
            options: Object.entries(column.enumVals).map((ev) => ({
              key: ev[0],
              display: ev[1],
            })),
          })
        );
      }
      setValueMapperControllers(controllers);
    }
  }, [
    setValueMapperControllers,
    mappingConfigurationStatus,
    mappingConfiguration,
  ]);

  // Handle non-streaming sheet analysis results
  useEffect(() => {
    if (useStreamingSheetAnalysis) return;

    if (sheetAnalysisQuery.status === "fulfilled" && sheetAnalysisQuery.data) {
      const analysisData =
        sheetAnalysisQuery.data.data || sheetAnalysisQuery.data;
      setIsAnalysisComplete(true);
      handleAnalysisResults(analysisData, false); // Non-streaming complete results
    } else if (sheetAnalysisQuery.status === "rejected") {
      setIsAnalyzing(false);
    }
  }, [
    sheetAnalysisQuery.status,
    sheetAnalysisQuery.data,
    useStreamingSheetAnalysis,
    handleAnalysisResults,
  ]);

  // Function to start PDF extraction
  const startPdfExtraction = useCallback(async (fileDownloadUrl) => {
    try {
      const result = await extractPdfData(fileDownloadUrl, {
        generateExcel: true,
        generatePdf: false,
        cleanTable: true,
        removeSubtotal: true,
      });

      if (result.success && result.data) {
        // Store extraction result and show PDF flow
        setPdfExtractionResult(result);
        setShowPdfFlow(true);
        setDialogOpen(true);
      } else {
        throw new Error(result.error || "PDF extraction failed");
      }
    } catch (error) {
      // Reset state on error
      setIsAnalyzing(false);
      setShowPdfFlow(false);
      // You might want to show an error message to the user here
    }
  }, []);

  // Start AI analysis when file is uploaded (if AI is enabled)
  useEffect(() => {
    const fileId = mappingConfig?.fileId;
    const filename = mappingConfig?.filename;

    if (
      fileId &&
      !fileDownloadUrl &&
      !isAnalyzing &&
      (useClaimsAIParsing || useAiPdfExtraction)
    ) {
      const fetchDownloadUrl = async () => {
        try {
          setIsAnalyzing(true);
          setLoadingPhase("analyzing");

          const response = await utils.authenticatedFetch(
            config.endpoints.files(`get-download-url/${fileId}`),
            {
              method: "GET",
              headers: { "Content-Type": "application/json" },
            }
          );
          if (!response.ok) throw new Error("Failed to get download URL");

          const data = await response.json();
          const url = data.url;
          setFileDownloadUrl(url);

          // Check if this is a PDF file
          const isPdf = filename && filename.toLowerCase().endsWith(".pdf");

          if (isPdf && useAiPdfExtraction) {
            // Keep upload box visible with loading while extracting
            setIsAnalyzing(true);
            setLoadingPhase("analyzing");
            // Start PDF extraction
            startPdfExtraction(url);
          } else if (useClaimsAIParsing && useStreamingSheetAnalysis) {
            // Process Excel/CSV with streaming analysis
            processStreamingAnalysis(url);
          }
        } catch (error) {
          setIsAnalyzing(false);
        }
      };

      fetchDownloadUrl();
    }
  }, [
    mappingConfig?.fileId,
    mappingConfig?.filename,
    fileDownloadUrl,
    isAnalyzing,
    useClaimsAIParsing,
    useAiPdfExtraction,
    useStreamingSheetAnalysis,
    processStreamingAnalysis,
    startPdfExtraction,
  ]);

  // Helper functions for per-sheet configuration
  const getCurrentSheetName = useCallback(() => {
    if (isMultiSheetMode && selectedSheets.length > 0) {
      return selectedSheets[currentSheetIndex] || mappingConfig?.tableName;
    }
    return mappingConfig?.tableName;
  }, [
    isMultiSheetMode,
    selectedSheets,
    currentSheetIndex,
    mappingConfig?.tableName,
  ]);

  // Helper function to get current sheet being processed
  const getCurrentProcessingSheetName = useCallback(() => {
    if (isMultiSheetMode && selectedSheets.length > 0) {
      return (
        selectedSheets[currentProcessingSheetIndex] || mappingConfig?.tableName
      );
    }
    return mappingConfig?.tableName;
  }, [
    isMultiSheetMode,
    selectedSheets,
    currentProcessingSheetIndex,
    mappingConfig?.tableName,
  ]);

  // Helper function to get current processing sheet config
  const getCurrentProcessingSheetConfig = useCallback(() => {
    const processingSheetName = getCurrentProcessingSheetName();
    if (isMultiSheetMode && processingSheetName) {
      const config = sheetConfigs[processingSheetName] || {};
      return config;
    }
    return mappingConfig || {};
  }, [
    isMultiSheetMode,
    getCurrentProcessingSheetName,
    sheetConfigs,
    mappingConfig,
  ]);

  const getCurrentSheetConfig = useCallback(() => {
    const currentSheetName = getCurrentSheetName();
    if (isMultiSheetMode && currentSheetName) {
      return sheetConfigs[currentSheetName] || {};
    }
    return mappingConfig || {};
  }, [isMultiSheetMode, getCurrentSheetName, sheetConfigs, mappingConfig]);

  const updateCurrentSheetConfig = useCallback(
    (updateFn) => {
      const currentSheetName = getCurrentSheetName();
      if (isMultiSheetMode && currentSheetName) {
        setSheetConfigs((prev) => ({
          ...prev,
          [currentSheetName]: updateFn(prev[currentSheetName] || {}),
        }));
      } else {
        setMappingConfig(updateFn);
      }
    },
    [isMultiSheetMode, getCurrentSheetName]
  );

  // Helper to validate all sheets have proper column mappings
  const validateAllSheetsColumnMappings = useCallback(() => {
    if (!isMultiSheetMode || selectedSheets.length <= 1) {
      setAllSheetsColumnMappingsValid(true);
      return true;
    }

    const isValid = selectedSheets.every((sheetName) => {
      const sheetConfig = sheetConfigs[sheetName];
      if (!sheetConfig?.mapping?.length) return false;

      // Check that all required columns are mapped
      return requiredColumns.every((col) =>
        sheetConfig.mapping.some((mapping) => mapping.outputName === col)
      );
    });

    setAllSheetsColumnMappingsValid(isValid);
    return isValid;
  }, [isMultiSheetMode, selectedSheets, sheetConfigs, requiredColumns]);

  // Helper to validate all sheets have proper value mappings
  const validateAllSheetsValueMappings = useCallback(() => {
    if (!isMultiSheetMode || selectedSheets.length <= 1) {
      setAllSheetsValueMappingsValid(true);
      return true;
    }

    const isValid = selectedSheets.every((sheetName) => {
      const sheetConfig = sheetConfigs[sheetName];
      if (!sheetConfig?.mapping) return false;

      const enumColumns = sheetConfig.mapping.filter((mapping) =>
        mapping.conversions?.some((conv) => conv.conversionType === "CONVERT")
      );

      // If there are no enum columns, this sheet is valid
      if (enumColumns.length === 0) return true;

      return enumColumns.every((mapping) => {
        const conversion = mapping.conversions?.find(
          (conv) => conv.conversionType === "CONVERT"
        );
        return (
          conversion &&
          ((conversion.conversions &&
            Object.keys(conversion.conversions).length > 0) ||
            conversion.value) // constant conversion
        );
      });
    });

    setAllSheetsValueMappingsValid(isValid);
    return isValid;
  }, [isMultiSheetMode, selectedSheets, sheetConfigs]);

  // Helper to update allValuesMapped state for multi-sheet mode
  const updateValueMappedState = useCallback(() => {
    if (isMultiSheetMode && selectedSheets.length > 1) {
      const allSheetsValid = validateAllSheetsValueMappings();
      setAllValuesMapped({ allSheets: allSheetsValid });
    }
  }, [isMultiSheetMode, selectedSheets, validateAllSheetsValueMappings]);

  // Update validation state when sheet configs change
  useEffect(() => {
    if (isMultiSheetMode && selectedSheets.length > 1) {
      validateAllSheetsColumnMappings();
      updateValueMappedState();
    }
  }, [
    isMultiSheetMode,
    selectedSheets,
    sheetConfigs,
    validateAllSheetsColumnMappings,
    updateValueMappedState,
  ]);

  // Update validation state when current sheet index changes (tab switching)
  useEffect(() => {
    if (isMultiSheetMode && selectedSheets.length > 1) {
      validateAllSheetsColumnMappings();
      validateAllSheetsValueMappings();
    }
  }, [
    currentSheetIndex,
    isMultiSheetMode,
    selectedSheets,
    validateAllSheetsColumnMappings,
    validateAllSheetsValueMappings,
  ]);

  // Update validation state when step changes (entering different steps)
  useEffect(() => {
    if (isMultiSheetMode && selectedSheets.length > 1) {
      validateAllSheetsColumnMappings();
      validateAllSheetsValueMappings();
    }
  }, [
    step,
    isMultiSheetMode,
    selectedSheets,
    validateAllSheetsColumnMappings,
    validateAllSheetsValueMappings,
  ]);

  // Initialize multi-sheet mode when sheets are selected
  useEffect(() => {
    if (
      selectedSheets.length > 0 &&
      sheetAnalysisResults &&
      !multiSheetConfig
    ) {
      const config = cdr.initializeMultiSheetMapping(mappingConfig, {
        fileId: mappingConfig.fileId,
        filename: mappingConfig.filename,
        selectedSheets,
        sheetAnalysisResults,
      });
      setMultiSheetConfig(config);
      setIsMultiSheetMode(true);

      // Initialize per-sheet configurations
      const initialConfigs = {};
      selectedSheets.forEach((sheetName) => {
        const sheetData = sheetAnalysisResults?.sheets?.find(
          (s) => s.sheet_name === sheetName
        );
        const origin = sheetData?.table_start
          ? {
              row: Math.max(0, (sheetData.table_start.row || 1) - 1),
              column: Math.max(0, (sheetData.table_start.column || 1) - 1),
            }
          : { row: 0, column: 0 };

        // Check if this sheet was already configured in single-sheet mode or previous multi-sheet mode
        const existingSheetConfig = sheetConfigs[sheetName];
        const isCurrentSingleSheet = mappingConfig?.tableName === sheetName;

        if (existingSheetConfig) {
          // Preserve existing configuration from previous multi-sheet mode
          initialConfigs[sheetName] = existingSheetConfig;
        } else if (isCurrentSingleSheet && mappingConfig?.mapping) {
          // Preserve configuration from current single-sheet mode
          initialConfigs[sheetName] = {
            ...mappingConfig,
            tableName: sheetName,
            origin: mappingConfig.origin || origin,
          };
        } else {
          // Create new configuration with defaults
          initialConfigs[sheetName] = {
            ...mappingConfig,
            tableName: sheetName,
            origin,
            mapping: [],
            substitutions: [],
            rowDeletions: [],
            dateConversionTypes: {},
          };
        }
      });
      setSheetConfigs(initialConfigs);
    }
  }, [
    selectedSheets,
    sheetAnalysisResults,
    multiSheetConfig,
    mappingConfig,
    sheetConfigs,
  ]);

  // Sync single-sheet changes to sheetConfigs to preserve work when switching modes
  useEffect(() => {
    if (
      !isMultiSheetMode &&
      mappingConfig?.tableName &&
      mappingConfig?.mapping?.length > 0
    ) {
      // Update sheetConfigs with current single-sheet configuration
      setSheetConfigs((prev) => ({
        ...prev,
        [mappingConfig.tableName]: {
          ...mappingConfig,
        },
      }));
    }
  }, [isMultiSheetMode, mappingConfig]);

  // Reset processing sheet index when entering multi-sheet mode
  useEffect(() => {
    if (step === 5 && isMultiSheetMode && selectedSheets.length > 1) {
      setCurrentProcessingSheetIndex(0);
      setCompletedSheets([]);

      // Validate that all sheets have proper mappings
      selectedSheets.forEach((sheetName) => {
        const sheetConfig = sheetConfigs[sheetName];
        if (!sheetConfig?.mapping || sheetConfig.mapping.length === 0) {
          // Sheet has no column mappings - this may cause processing errors
        }
      });
    }
  }, [step, isMultiSheetMode, selectedSheets, sheetConfigs]);

  const reset = useCallback(() => {
    try {
      abortCommand();
      setStep(0);
      setDialogOpen(false);
      setMappingConfig(cdr.clearMapping());
      setTablesList([]);
      setTargetColumnMap({});
      setTargetColumnList([]);
      setSourceHeaders([]);
      setAllValuesMapped({});
      setAppliedExcessValid(true);
      setIsConversionComplete(false);
      setFileDownloadUrl(null);
      setSheetAnalysisResults(null);
      setAnalysisHeaders(null);
      setIsAnalyzing(false);
      setIsAnalysisComplete(false);
      setSelectedSheets([]);
      setIsMultiSheetMode(false);
      setMultiSheetConfig(null);
      setCurrentSheetIndex(0);
      setSheetConfigs({});
      setCurrentProcessingSheetIndex(0);
      setCompletedSheets([]);
      setAllSheetsColumnMappingsValid(true);
      setAllSheetsValueMappingsValid(true);
      // Reset PDF state
      setShowPdfFlow(false);
      setPdfExtractionResult(null);
    } catch (error) {
      // Continue with basic reset even if something fails
      try {
        setDialogOpen(false);
        setStep(0);
        setIsConversionComplete(false);
      } catch (basicResetError) {
        // Basic reset failed
      }
    }
  }, []);

  // Handle sheet selection
  const handleSheetSelection = useCallback(
    (sheets) => {
      // Only allow multi-sheet mode when AI parsing feature flag is enabled
      if (!useClaimsAIParsing && sheets.length > 1) {
        return;
      }

      setSelectedSheets(sheets);
      if (sheets.length === 1) {
        // Single sheet mode
        setIsMultiSheetMode(false);
        const sheetName = sheets[0];
        const sheetData = sheetAnalysisResults?.sheets?.find(
          (s) => s.sheet_name === sheetName
        );

        // Check if we have existing configuration for this sheet from previous multi-sheet mode
        const existingSheetConfig = sheetConfigs[sheetName];

        if (existingSheetConfig) {
          // Preserve existing configuration from multi-sheet mode
          setMappingConfig({
            ...existingSheetConfig,
            fileId: mappingConfig?.fileId || existingSheetConfig.fileId,
            filename: mappingConfig?.filename || existingSheetConfig.filename,
          });
        } else {
          // No existing config, set up basic configuration
          setMappingConfig((cd) =>
            cdr.setTableName(cd, { tableName: sheetName })
          );

          if (sheetData?.table_start) {
            const row = Math.max(0, (sheetData.table_start.row || 1) - 1);
            const column = Math.max(0, (sheetData.table_start.column || 1) - 1);
            setMappingConfig((cd) =>
              cdr.setOrigin(cd, { origin: { row, column } })
            );
          } else {
            setMappingConfig((cd) =>
              cdr.setOrigin(cd, { origin: { row: 0, column: 0 } })
            );
          }
        }

        if (sheetData?.header_row) {
          setAnalysisHeaders(sheetData.header_row);
        }
      } else if (sheets.length > 1 && useClaimsAIParsing) {
        // Multi-sheet mode (only when feature flag is enabled)
        setIsMultiSheetMode(true);
        setMultiSheetConfig(null); // Will be initialized in useEffect
      }
    },
    [sheetAnalysisResults, useClaimsAIParsing, sheetConfigs, mappingConfig]
  );

  // Table summary query
  const tableSummaryRequest = useMemo(() => {
    // For multi-sheet mode, use current sheet config; for single-sheet, use mappingConfig
    const config = isMultiSheetMode ? getCurrentSheetConfig() : mappingConfig;
    return {
      fileId: mappingConfig?.fileId,
      skipColumns: config?.origin?.column ?? 0,
      skipRows: config?.origin?.row ?? 0,
      tableName: config?.tableName,
      headerRows: config?.headerRows,
    };
  }, [mappingConfig, isMultiSheetMode, getCurrentSheetConfig]);

  const {
    data: tableSummaryData,
    status: tableSummaryStatus,
  } = useGetTableSummaryQuery(tableSummaryRequest, {
    skip:
      step < getChooseColumnsStepIndex() ||
      !tableSummaryRequest.fileId ||
      !tableSummaryRequest.tableName,
  });

  const tableSummary = useMemo(
    () => (tableSummaryStatus === "fulfilled" && tableSummaryData) || [],
    [tableSummaryData, tableSummaryStatus]
  );

  const getLoadingMessage = useCallback(() => {
    const filename = mappingConfig?.filename || "file";
    switch (loadingPhase) {
      case "uploading":
        return `Uploading ${filename}`;
      case "analyzing":
        return `Analyzing ${filename}`;
      case "extracting":
        return `Extracting data from ${filename}`;
      case "processing":
        return `Processing results`;
      case "completed":
        return `Processing complete`;
      default:
        return `Loading ${filename}`;
    }
  }, [mappingConfig?.filename, loadingPhase]);

  // Determine if we should show multi-sheet selector (only when there are multiple sheets)
  const shouldShowMultiSheetSelector =
    useClaimsAIParsing &&
    sheetAnalysisResults &&
    isAnalysisComplete &&
    sheetAnalysisResults.sheets &&
    sheetAnalysisResults.sheets.length > 1;

  // Auto-select single sheet when AI parsing is enabled and there's only one sheet
  useEffect(() => {
    if (
      useClaimsAIParsing &&
      sheetAnalysisResults &&
      isAnalysisComplete &&
      sheetAnalysisResults.sheets &&
      sheetAnalysisResults.sheets.length === 1 &&
      selectedSheets.length === 0
    ) {
      const singleSheet = sheetAnalysisResults.sheets[0].sheet_name;
      handleSheetSelection([singleSheet]);

      // Auto-advance to Choose Columns step and open dialog
      const chooseColumnsStep = getChooseColumnsStepIndex();
      if (step <= 1) {
        setTimeout(() => {
          setStep(chooseColumnsStep);
          setDialogOpen(true);
        }, 100);
      }
    } else if (
      useClaimsAIParsing &&
      sheetAnalysisResults &&
      isAnalysisComplete &&
      sheetAnalysisResults.sheets &&
      sheetAnalysisResults.sheets.length > 1
    ) {
      // Multiple sheets: go to Choose Data step
      const chooseDataStep = getChooseDataStepIndex();
      if (step <= 1 && chooseDataStep !== -1) {
        setTimeout(() => {
          setStep(chooseDataStep);
          setDialogOpen(true);
        }, 100);
      }
    }
  }, [
    useClaimsAIParsing,
    sheetAnalysisResults,
    isAnalysisComplete,
    selectedSheets.length,
    step,
    handleSheetSelection,
    getChooseColumnsStepIndex,
    getChooseDataStepIndex,
  ]);

  // Create all possible steps (moved into useMemo to fix dependency warning)
  const steps = useMemo(() => {
    const allSteps = [
      {
        label: "Upload Data",
        description: "Upload your Excel file",
        component: (
          <CentredLoader
            label={getLoadingMessage()}
            showProgress={["analyzing", "extracting"].includes(loadingPhase)}
            useEnhancedAnimation={useClaimsAIParsing || useAiPdfExtraction}
          />
        ),
        // Progression from the upload/loading stage is automatic, so Next is
        // always disabled here (Cancel remains available to abort).
        nextDisabled: () => true,
      },

      {
        label: "Choose Data",
        title: "Choose Data",
        description: "Select the table and data range",
        component: shouldShowMultiSheetSelector ? (
          <MultiSheetSelector
            sheetAnalysisResults={sheetAnalysisResults}
            selectedSheets={selectedSheets}
            onSelectionChange={handleSheetSelection}
          />
        ) : (
          <SheetSelector
            fileId={mappingConfig?.fileId}
            tableName={mappingConfig?.tableName}
            origin={mappingConfig?.origin}
            setTableName={(tableName) =>
              setMappingConfig((cd) => cdr.setTableName(cd, { tableName }))
            }
            setOrigin={(origin) =>
              setMappingConfig((cd) => cdr.setOrigin(cd, { origin }))
            }
            setTablesList={setTablesList}
          />
        ),
        nextDisabled: () =>
          shouldShowMultiSheetSelector
            ? selectedSheets.length === 0
            : !mappingConfig?.tableName,
      },
      {
        label: "Choose Columns",
        title: "Choose Columns",
        description: "Select columns to import",
        component: (
          <ColumnMapper
            fileId={mappingConfig?.fileId}
            origin={getCurrentSheetConfig()?.origin || mappingConfig?.origin}
            tableName={
              getCurrentSheetConfig()?.tableName || mappingConfig?.tableName
            }
            headerRows={
              getCurrentSheetConfig()?.headerRows || mappingConfig?.headerRows
            }
            mapping={getCurrentSheetConfig()?.mapping || mappingConfig?.mapping}
            analysisHeaders={analysisHeaders}
            updateColumns={(mappingList) => {
              // Apply mappings only to the current sheet (whether single or multi-sheet mode)
              mappingList.forEach((columnMap) =>
                updateCurrentSheetConfig((cd) =>
                  cdr.updateColumnMapping(cd, { ...columnMap })
                )
              );

              // Update validation state if in multi-sheet mode
              if (isMultiSheetMode && selectedSheets.length > 1) {
                setTimeout(() => {
                  validateAllSheetsColumnMappings();
                  updateValueMappedState();
                }, 0);
              }
            }}
            targetColumnList={targetColumnList}
            setTargetColumnList={setTargetColumnList}
            targetColumnMap={targetColumnMap}
            setTargetColumnMap={setTargetColumnMap}
            sourceHeaders={sourceHeaders}
            setSourceHeaders={setSourceHeaders}
            outputColumns={outputColums}
            tableSummary={tableSummary}
            parentRef={dialogContentRef}
            setTableName={(tableName) =>
              updateCurrentSheetConfig((cd) =>
                cdr.setTableName(cd, { tableName })
              )
            }
            setOrigin={(origin) =>
              updateCurrentSheetConfig((cd) => cdr.setOrigin(cd, { origin }))
            }
            setTableNameAndOrigin={(tableName, origin) => {
              updateCurrentSheetConfig((cd) => {
                const withNewTable = cdr.setTableName(cd, { tableName });
                setAnalysisHeaders(null);
                return cdr.setOrigin(withNewTable, { origin });
              });
            }}
            sheetAnalysisResults={sheetAnalysisResults}
            useSheetAnalysisWorkflow={useClaimsAIParsing}
            isAnalysisComplete={isAnalysisComplete}
            // Multi-sheet props
            isMultiSheetMode={isMultiSheetMode}
            multiSheetConfig={multiSheetConfig}
            currentSheetIndex={currentSheetIndex}
            setCurrentSheetIndex={setCurrentSheetIndex}
            selectedSheets={selectedSheets}
          />
        ),
        nextDisabled: () => {
          if (isMultiSheetMode && selectedSheets.length > 1) {
            // Multi-sheet mode: apply single-sheet validation logic to ALL selected sheets
            return (
              !targetColumnList?.length ||
              !sourceHeaders?.length ||
              !selectedSheets.every((sheetName) => {
                const sheetConfig = sheetConfigs[sheetName];
                return (
                  sheetConfig?.mapping?.length &&
                  requiredColumns.every((col) =>
                    sheetConfig.mapping.some(
                      (mapping) => mapping.outputName === col
                    )
                  )
                );
              })
            );
          } else {
            // Single sheet mode: original validation logic
            const currentConfig = getCurrentSheetConfig();
            const isDisabled =
              !targetColumnList?.length ||
              !sourceHeaders?.length ||
              !currentConfig?.mapping?.length ||
              !requiredColumns.every((col) =>
                currentConfig?.mapping?.some(
                  (mapping) => mapping.outputName === col
                )
              );
            return isDisabled;
          }
        },
      },
      {
        label: "Map Values",
        title: "Map Values",
        description: "Map column values",
        component: (
          <ValueMapper
            fileId={mappingConfig?.fileId}
            origin={getCurrentSheetConfig()?.origin || mappingConfig?.origin}
            tableName={
              getCurrentSheetConfig()?.tableName || mappingConfig?.tableName
            }
            headerRows={
              getCurrentSheetConfig()?.headerRows || mappingConfig?.headerRows
            }
            mapping={getCurrentSheetConfig()?.mapping || mappingConfig?.mapping}
            controllers={valueMapperControllers}
            updateValueMapping={(list) => {
              // Apply value mappings only to the current sheet (whether single or multi-sheet mode)
              list.forEach((vm) => {
                const currentConfig = getCurrentSheetConfig();
                const mapping =
                  currentConfig?.mapping || mappingConfig?.mapping;
                const columnMapping = mapping?.find(
                  (m) => m.outputName === vm.outputName
                );

                if (
                  columnMapping?.conversions?.[0]?.conversionType === "CONVERT"
                ) {
                  updateCurrentSheetConfig((cd) =>
                    cdr.updateConversionMapping(cd, { ...vm })
                  );
                }
              });

              // Update validation state if in multi-sheet mode
              if (isMultiSheetMode && selectedSheets.length > 1) {
                setTimeout(() => {
                  validateAllSheetsValueMappings();
                  updateValueMappedState();
                }, 0);
              }
            }}
            setAllValuesMapped={setAllValuesMapped}
            updateConstantConversion={(outputName, value) => {
              updateCurrentSheetConfig((cd) =>
                cdr.updateConstantConversionValue(cd, { outputName, value })
              );

              // Update validation state if in multi-sheet mode
              if (isMultiSheetMode && selectedSheets.length > 1) {
                setTimeout(() => {
                  validateAllSheetsValueMappings();
                  updateValueMappedState();
                }, 0);
              }
            }}
            // Multi-sheet props
            isMultiSheetMode={isMultiSheetMode}
            multiSheetConfig={multiSheetConfig}
            currentSheetIndex={currentSheetIndex}
            setCurrentSheetIndex={setCurrentSheetIndex}
            selectedSheets={selectedSheets}
            sheetAnalysisResults={sheetAnalysisResults}
            allValuesMapped={allValuesMapped}
          />
        ),
        nextDisabled: () => {
          if (mappingConfigurationStatus !== "fulfilled") {
            return true;
          }

          const hasAnyMappingAllIgnored = valueMapperControllers?.some(
            (controller) =>
              allValuesMapped[`${controller.key}_allIgnored`] === true
          );
          if (hasAnyMappingAllIgnored) {
            return true;
          }

          if (isMultiSheetMode && selectedSheets.length > 1) {
            // Multi-sheet mode: validate ALL selected sheets directly
            const allSheetsValid = selectedSheets.every((sheetName) => {
              const sheetConfig = sheetConfigs[sheetName];
              if (!sheetConfig?.mapping) return false;

              // Find enum columns that need value mappings
              const enumColumns = sheetConfig.mapping.filter((mapping) =>
                mapping.conversions?.some(
                  (conv) => conv.conversionType === "CONVERT"
                )
              );

              // If there are no enum columns, this sheet is valid
              if (enumColumns.length === 0) return true;

              // Check that all enum columns have proper value mappings
              return enumColumns.every((mapping) => {
                const conversion = mapping.conversions?.find(
                  (conv) => conv.conversionType === "CONVERT"
                );
                return (
                  conversion &&
                  ((conversion.conversions &&
                    Object.keys(conversion.conversions).length > 0) ||
                    conversion.value) // constant conversion
                );
              });
            });

            return !allSheetsValid;
          } else {
            // Single sheet mode: original validation logic
            return !Object.entries(allValuesMapped)
              .filter(([key]) => !key.endsWith("_allIgnored"))
              .every(([, columnMapped]) => columnMapped);
          }
        },
      },
      {
        label: "Other Details",
        title: "Other Details",
        description: "Configure additional settings",
        component: (
          <AppliedExcessRecorder
            appliedExcess={
              isMultiSheetMode
                ? multiSheetConfig?.appliedExcess
                : mappingConfig?.appliedExcess
            }
            updateAppliedExcess={(appliedExcess) => {
              if (isMultiSheetMode) {
                setMultiSheetConfig((cd) => ({
                  ...cd,
                  appliedExcess,
                }));
              } else {
                setMappingConfig((cd) =>
                  cdr.setAppliedExcess(cd, { appliedExcess })
                );
              }
            }}
            setAppliedExcessValid={(isValid) => setAppliedExcessValid(isValid)}
          />
        ),
        nextDisabled: () => !appliedExcessValid,
      },
      {
        label: "Consistency Checks",
        title: "Consistency Checks",
        description:
          isMultiSheetMode && selectedSheets.length > 1
            ? `Processing sheet: ${getCurrentProcessingSheetName()}`
            : "Validating and importing claims data",
        component: (
          <ClaimsTransformer
            fileId={mappingConfig?.fileId}
            origin={
              getCurrentProcessingSheetConfig()?.origin || mappingConfig?.origin
            }
            tableName={
              getCurrentProcessingSheetConfig()?.tableName ||
              mappingConfig?.tableName
            }
            headerRows={
              getCurrentProcessingSheetConfig()?.headerRows ||
              mappingConfig?.headerRows
            }
            mapping={
              getCurrentProcessingSheetConfig()?.mapping ||
              mappingConfig?.mapping
            }
            appliedExcess={
              isMultiSheetMode
                ? multiSheetConfig?.appliedExcess
                : mappingConfig?.appliedExcess
            }
            substitutions={
              selectedSheets.length > 1
                ? getCurrentProcessingSheetConfig()?.substitutions || []
                : mappingConfig?.substitutions || []
            }
            rowDeletions={
              selectedSheets.length > 1
                ? getCurrentProcessingSheetConfig()?.rowDeletions || []
                : mappingConfig?.rowDeletions || []
            }
            filename={mappingConfig?.filename}
            originalFileId={mappingConfig?.originalFileId}
            originalFilename={mappingConfig?.originalFilename}
            extractionDetails={mappingConfig?.extractionDetails}
            tablesList={tablesList || []}
            // Enhanced progress tracking props
            isMultiSheet={isMultiSheetMode && selectedSheets.length > 1}
            currentSheetIndex={currentProcessingSheetIndex}
            totalSheets={selectedSheets.length}
            completedSheets={completedSheets}
            onConversionComplete={() => {
              try {
                if (isMultiSheetMode && selectedSheets.length > 1) {
                  // Multi-sheet processing
                  const currentSheetName = getCurrentProcessingSheetName();
                  const newCompletedSheets = [
                    ...completedSheets,
                    currentSheetName,
                  ];
                  setCompletedSheets(newCompletedSheets);

                  if (currentProcessingSheetIndex < selectedSheets.length - 1) {
                    // Move to next sheet with a small delay to avoid race conditions
                    setTimeout(() => {
                      setCurrentProcessingSheetIndex(
                        currentProcessingSheetIndex + 1
                      );
                    }, 100);
                  } else {
                    // All sheets completed
                    setTimeout(() => {
                      reset();
                      setIsConversionComplete(true);
                    }, 500);
                  }
                } else {
                  // Single sheet processing
                  reset();
                  setIsConversionComplete(true);
                }
              } catch (error) {
                // Handle the error gracefully instead of crashing
                setIsConversionComplete(true);
              }
            }}
            dateConversionTypeList={
              selectedSheets.length > 1
                ? getCurrentProcessingSheetConfig()?.dateConversionTypes || {}
                : mappingConfig?.dateConversionTypes || {}
            }
            setDateConversionType={(column, type, sheetName = null) => {
              if (selectedSheets.length > 1 && sheetName) {
                // Multi-sheet mode: use provided sheet name
                setSheetConfigs((prev) => ({
                  ...prev,
                  [sheetName]: {
                    ...prev[sheetName],
                    dateConversionTypes: {
                      ...prev[sheetName]?.dateConversionTypes,
                      [column]: type,
                    },
                  },
                }));
              } else if (selectedSheets.length > 1) {
                // Fallback: use current processing sheet
                const currentSheetName = getCurrentProcessingSheetName();
                setSheetConfigs((prev) => ({
                  ...prev,
                  [currentSheetName]: {
                    ...prev[currentSheetName],
                    dateConversionTypes: {
                      ...prev[currentSheetName]?.dateConversionTypes,
                      [column]: type,
                    },
                  },
                }));
              } else {
                setMappingConfig((cd) =>
                  cdr.updateDateConversionType(cd, { column, type })
                );
              }
            }}
            onSubstitutionsChanged={(substitutions, sheetName = null) => {
              if (selectedSheets.length > 1 && sheetName) {
                // Multi-sheet mode: use provided sheet name
                setSheetConfigs((prev) => ({
                  ...prev,
                  [sheetName]: {
                    ...prev[sheetName],
                    substitutions,
                  },
                }));
              } else if (selectedSheets.length > 1) {
                // Fallback: use current processing sheet
                const currentSheetName = getCurrentProcessingSheetName();
                setSheetConfigs((prev) => ({
                  ...prev,
                  [currentSheetName]: {
                    ...prev[currentSheetName],
                    substitutions,
                  },
                }));
              } else {
                setMappingConfig((cd) =>
                  cdr.setSubstitutions(cd, { substitutions })
                );
              }
            }}
            onRowDeletionsChanged={(rowDeletions, sheetName = null) => {
              if (selectedSheets.length > 1 && sheetName) {
                // Multi-sheet mode: use provided sheet name
                setSheetConfigs((prev) => ({
                  ...prev,
                  [sheetName]: {
                    ...prev[sheetName],
                    rowDeletions,
                  },
                }));
              } else if (selectedSheets.length > 1) {
                // Fallback: use current processing sheet
                const currentSheetName = getCurrentProcessingSheetName();
                setSheetConfigs((prev) => ({
                  ...prev,
                  [currentSheetName]: {
                    ...prev[currentSheetName],
                    rowDeletions,
                  },
                }));
              } else {
                setMappingConfig((cd) =>
                  cdr.setRowDeletions(cd, { rowDeletions })
                );
              }
            }}
            selectedSheets={selectedSheets}
            setCurrentSheetIndex={setCurrentProcessingSheetIndex}
            sheetConfigs={sheetConfigs}
          />
        ),
        nextDisabled: () => true,
      },
    ];

    // Filter steps based on whether Choose Data step should be shown
    if (shouldShowChooseDataStep) {
      return allSteps;
    } else {
      // Skip the "Choose Data" step (index 1) when AI parsing is enabled and single sheet
      return [allSteps[0], ...allSteps.slice(2)];
    }
  }, [
    shouldShowChooseDataStep,
    getLoadingMessage,
    loadingPhase,
    useClaimsAIParsing,
    useAiPdfExtraction,
    shouldShowMultiSheetSelector,
    sheetAnalysisResults,
    selectedSheets,
    handleSheetSelection,
    mappingConfig,
    setMappingConfig,
    setTablesList,
    getCurrentSheetConfig,
    analysisHeaders,
    updateCurrentSheetConfig,
    validateAllSheetsColumnMappings,
    updateValueMappedState,
    isMultiSheetMode,
    targetColumnList,
    setTargetColumnList,
    targetColumnMap,
    setTargetColumnMap,
    sourceHeaders,
    setSourceHeaders,
    outputColums,
    tableSummary,
    dialogContentRef,
    setAnalysisHeaders,
    isAnalysisComplete,
    multiSheetConfig,
    currentSheetIndex,
    setCurrentSheetIndex,
    requiredColumns,
    sheetConfigs,
    valueMapperControllers,
    setAllValuesMapped,
    validateAllSheetsValueMappings,
    mappingConfigurationStatus,
    allValuesMapped,
    appliedExcessValid,
    setAppliedExcessValid,
    currentProcessingSheetIndex,
    getCurrentProcessingSheetName,
    getCurrentProcessingSheetConfig,
    tablesList,
    completedSheets,
    setCompletedSheets,
    setCurrentProcessingSheetIndex,
    reset,
    setIsConversionComplete,
  ]);

  const onPrev = () => {
    setStep(Math.max(step - 1, 1));
  };

  const onNext = () => {
    setStep(Math.min(step + 1, steps.length - 1));
  };

  const activeStep = steps[step];
  const aborter = useRef(null);

  const nextDisabled =
    step >= steps.length - 1
      ? true
      : typeof steps[step].nextDisabled === "function"
      ? steps[step].nextDisabled()
      : false;

  const abortCommand = () => {
    if (aborter.current) aborter.current.abort();
  };

  // Show PDF extraction flow when enabled
  if (showPdfFlow && pdfExtractionResult) {
    return (
      <PdfExtractionFlow
        fileId={mappingConfig?.fileId}
        filename={mappingConfig?.filename}
        fileDownloadUrl={fileDownloadUrl}
        extractionResult={pdfExtractionResult}
        skipExtraction={true}
        onApprove={(convertedAnalysis) => {
          // Hide PDF flow and start AI analysis
          setShowPdfFlow(false);

          // If CSV file ID is provided, switch to using the CSV file for processing
          // but store both file IDs for download purposes and extraction details
          if (convertedAnalysis.csvFileId) {
            setMappingConfig((cd) =>
              cdr.setFileId(cd, { fileId: convertedAnalysis.csvFileId })
            );
            setMappingConfig((cd) =>
              cdr.setFilename(cd, { filename: "original.csv" })
            );
            // Store the original PDF file ID for downloads
            setMappingConfig((cd) =>
              cdr.setOriginalFileId(cd, {
                originalFileId: convertedAnalysis.originalPdfFileId,
              })
            );
            setMappingConfig((cd) =>
              cdr.setOriginalFilename(cd, {
                originalFilename: mappingConfig?.filename,
              })
            );
            // Store the extraction details for audit trail
            setMappingConfig((cd) =>
              cdr.setExtractionDetails(cd, {
                extractionDetails: convertedAnalysis.extractionDetails,
              })
            );
            // Clear the old PDF file download URL so it gets refreshed for the CSV
            setFileDownloadUrl(null);
          }

          if (useClaimsAIParsing) {
            // Start AI claims processing with the converted data
            setIsAnalyzing(true);
            setLoadingPhase("analyzing");
            setStep(0); // Go to loading step
            setDialogOpen(true);

            // Process the converted data through AI claims analysis
            setTimeout(() => {
              handleAnalysisResults(convertedAnalysis, false);
            }, 100);
          } else {
            // If AI parsing is disabled, proceed directly to data selection
            setSheetAnalysisResults(convertedAnalysis);
            setTablesList(convertedAnalysis.sheets.map((s) => s.sheet_name));
            setIsAnalysisComplete(true);

            const chooseDataStep = getChooseDataStepIndex();
            if (chooseDataStep !== -1) {
              setStep(chooseDataStep);
            } else {
              setStep(getChooseColumnsStepIndex());
            }
            setDialogOpen(true);
          }
        }}
        onCancel={() => {
          // Reset and go back to upload
          setShowPdfFlow(false);
          setPdfExtractionResult(null);
          reset();
        }}
      />
    );
  }

  return (
    <>
      {!noDropBox && (
        <ClaimsUpload
          uploadCompleted={(fileId, _) => {
            setMappingConfig((cd) => cdr.setFileId(cd, { fileId }));
            if (!useClaimsAIParsing && !useAiPdfExtraction) {
              const chooseDataStep = getChooseDataStepIndex();
              if (chooseDataStep !== -1) {
                setStep(chooseDataStep);
              } else {
                setStep(getChooseColumnsStepIndex());
              }
              setDialogOpen(true);
            }
            setLoadingPhase("analyzing");
          }}
          uploadStarted={(file) => {
            setMappingConfig((cd) =>
              cdr.setFilename(cd, { filename: file.name })
            );
            if (!useClaimsAIParsing && !useAiPdfExtraction) {
              setDialogOpen(true);
              setStep(0);
            }
            setLoadingPhase("uploading");
          }}
          aborter={aborter}
          isAnalyzing={isAnalyzing}
          loadingMessage={getLoadingMessage()}
          useClaimsAIParsing={useClaimsAIParsing}
          useAiPdfExtraction={useAiPdfExtraction}
        />
      )}

      {isConversionComplete && (
        <Snackbar
          open={isConversionComplete}
          autoHideDuration={3500}
          TransitionComponent={Slide}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          onClose={() => setIsConversionComplete(false)}
        >
          <Alert severity={"success"} variant={"filled"}>
            {(() => {
              try {
                if (isMultiSheetMode && selectedSheets.length > 1) {
                  return `Claims data imported from ${selectedSheets.length} sheets`;
                } else {
                  return "Claims data imported";
                }
              } catch (error) {
                return "Claims data imported";
              }
            })()}
          </Alert>
        </Snackbar>
      )}

      {dialogOpen && (
        <Dialog
          open
          fullWidth
          maxWidth={false}
          PaperProps={{ sx: { minHeight: "90vh" } }}
        >
          <DialogTitle>{activeStep.title || activeStep.label}</DialogTitle>
          {/* position: relative contains CentredLoader's absolute overlay to
              the content area so it can't cover the action bar below. */}
          <DialogContent ref={dialogContentRef} sx={{ position: "relative" }}>
            {activeStep.component}
          </DialogContent>
          <DialogActions sx={{ minHeight: "3.625rem" }}>
            <>
              <Button
                onClick={reset}
                disableElevation
                variant={"contained"}
                color={"secondary"}
              >
                {"Cancel"}
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              {step < steps.length - 1 && (
                <>
                  <Button
                    onClick={onPrev}
                    disableElevation
                    variant={"contained"}
                    color={"primary"}
                    disabled={step < 2}
                  >
                    {"Prev"}
                  </Button>
                  <Button
                    onClick={onNext}
                    disableElevation
                    variant={"contained"}
                    color={"primary"}
                    disabled={nextDisabled}
                  >
                    {"Next"}
                  </Button>
                </>
              )}
            </>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default ClaimsDialog;
